import React from 'react';
import { Search, Plus, Minus, ShoppingBag, Send } from 'lucide-react';
import { formatPrice } from '../../utils/translations';
import { getCategoryIcon } from './posConstants';

export const MenuView = ({
  searchQuery, setSearchQuery, activeCategory, setActiveCategory,
  categories, filteredItems, cart, addToCart, updateCartQty,
  tableInput, setTableInput, discountPercent, setDiscountPercent,
  subtotal, discount, total, createOrder, isProcessing, t
}) => {
  return (
    <div className="flex-1 flex overflow-hidden" data-testid="menu-view">
      {/* Menu Section */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-slate-800">
        {/* Search & Categories */}
        <div className="p-4 border-b border-slate-800 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder={t.searchMenu}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border-none rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500/50"
              data-testid="menu-search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${
                activeCategory === 'all' ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
              data-testid="category-all"
            >
              {t.allCategories}
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  activeCategory === cat ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
                data-testid={`category-${cat}`}
              >
                {getCategoryIcon(cat)}
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredItems.map(item => {
              const itemName = item.name?.en || item.name || '';
              const inCart = cart.find(c => c.id === item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => addToCart(item)}
                  className={`bg-slate-800 rounded-xl p-3 cursor-pointer hover:bg-slate-750 transition-all active:scale-95 border ${
                    inCart ? 'border-amber-500' : 'border-slate-700'
                  }`}
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="relative h-20 bg-slate-700 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                    {item.image_url ? (
                      <img src={item.image_url} alt={itemName} className="w-full h-full object-cover" />
                    ) : (
                      getCategoryIcon(item.category)
                    )}
                    {inCart && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-amber-500 text-slate-900 rounded-full flex items-center justify-center text-xs font-black">
                        {inCart.quantity}
                      </div>
                    )}
                  </div>
                  <h4 className="font-bold text-sm truncate">{itemName}</h4>
                  <p className="text-amber-400 font-bold text-sm">{formatPrice(item.price)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      <div className="w-80 bg-slate-950 flex flex-col shrink-0" data-testid="cart-sidebar">
        {/* Table Input */}
        <div className="p-4 border-b border-slate-800">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{t.tableNumber}</label>
          <input
            type="text"
            value={tableInput}
            onChange={(e) => setTableInput(e.target.value)}
            placeholder="01, VIP, ..."
            className="w-full px-4 py-3 bg-slate-800 rounded-xl text-white font-bold text-lg focus:ring-2 focus:ring-amber-500/50 border-none"
            data-testid="table-input"
          />
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <h3 className="text-sm font-bold text-slate-500 uppercase">{t.newItems} ({cart.length})</h3>
          {cart.length === 0 ? (
            <div className="text-center py-8 text-slate-600">
              <ShoppingBag size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t.empty}</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="bg-slate-800 rounded-xl p-3" data-testid={`cart-item-${item.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-sm truncate flex-1">{item.name?.en || item.name}</h4>
                  <span className="font-bold text-amber-400">{formatPrice(item.price * item.quantity)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); updateCartQty(item.id, -1); }} className="w-7 h-7 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-slate-600">
                    <Minus size={14} />
                  </button>
                  <span className="font-bold w-8 text-center">{item.quantity}</span>
                  <button onClick={(e) => { e.stopPropagation(); updateCartQty(item.id, 1); }} className="w-7 h-7 bg-amber-500 text-slate-900 rounded-lg flex items-center justify-center hover:bg-amber-400">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Cart Footer */}
        <div className="p-4 bg-slate-900 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-500">{t.discount}</span>
            <div className="flex-1 flex bg-slate-800 rounded-lg overflow-hidden">
              {[0, 5, 10, 15, 20].map(pct => (
                <button
                  key={pct}
                  onClick={() => setDiscountPercent(pct)}
                  className={`flex-1 py-1.5 text-xs font-bold ${discountPercent === pct ? 'bg-amber-500 text-slate-900' : 'text-slate-500 hover:text-white'}`}
                  data-testid={`cart-discount-${pct}`}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t.subtotal}</span>
              <span className="font-bold">{formatPrice(subtotal)}</span>
            </div>
            {discountPercent > 0 && (
              <div className="flex justify-between text-sm text-emerald-400">
                <span>{t.discount} ({discountPercent}%)</span>
                <span>-{formatPrice(discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg pt-2 border-t border-slate-800">
              <span className="font-bold">{t.total}</span>
              <span className="font-black text-amber-400">{formatPrice(total)}</span>
            </div>
          </div>
          <button
            onClick={createOrder}
            disabled={cart.length === 0 || !tableInput || isProcessing}
            className="w-full py-3 bg-amber-500 text-slate-900 rounded-xl font-black hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="send-to-kitchen-btn"
          >
            {isProcessing ? (
              <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send size={18} />
                {t.sendToKitchen}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
